import { createShouldForwardPropBlockList, deepMerge } from "@components/common/utils/helper";
import {
    getBackgroundColor,
    getBorderColor,
    getBorderRadius,
    getBoxShadow,
    getBoxShadowFocus,
    getColor,
    getFontSize,
    getPaddingX,
    getPaddingY,
} from "./input-text.styles.utils";
import { type InputTextProps } from "./input-text";
import styled from "styled-components";

type WrapperProps = Pick<
    InputTextProps,
    "outline" | "outlineKeepOnHover" | "underline" | "size" | "length" | "rounded" | "color" | "shadow" | "clean" | "disabled" | "error" | "block"
> & {
    active?: boolean;
    isFocused: boolean;
};
type InputProps = Pick<InputTextProps, "size" | "resize" | "minRows">;

export const Container = styled("div")(() => ({
    position: "relative",
}));

export const Wrapper = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList([
        "outline",
        "outlineKeepOnHover",
        "underline",
        "size",
        "length",
        "rounded",
        "color",
        "shadow",
        "clean",
        "disabled",
        "error",
        "block",
        "isFocused",
    ]),
})<WrapperProps>(
    ({ outline, outlineKeepOnHover, underline, size, length, rounded, color, shadow, clean, disabled, error, block, isFocused, theme }) => ({
        transitionProperty: "background-color, box-shadow, color, opacity, transform, border",
        transitionDuration: "300ms",
        transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
        ...deepMerge({
            ...getBackgroundColor(outline, underline, color, clean, theme),
            ...getColor(outline, underline, outlineKeepOnHover, color, clean, theme),
            ...getBorderColor(color, clean, theme),
            ...(block && { width: "100%" }),
            ...(underline ? { borderTopColor: "transparent", borderRightColor: "transparent", borderLeftColor: "transparent" } : {}),
            display: "flex",
            gap: theme.spacing[3],
            position: "relative",
            outline: "none",
            whiteSpace: "nowrap",
            justifyContent: "center",
            borderWidth: theme.borderWidth[2],
            ...(underline && { borderLeftWidth: 0, borderRightWidth: 0 }),
            ...(disabled && { pointerEvents: "none", opacity: 0.5 }),
            ...(length && { width: `${length}px` }),
            ...getFontSize(size, theme),
            ...getPaddingX(underline, size, clean, theme),
            ...getPaddingY(underline, size, theme),
            ...getBorderRadius(rounded, theme),
            ...getBoxShadow(shadow, color, error, theme),
            ...(isFocused && {
                ...getBoxShadowFocus(color, underline, clean, error, theme),
                ...(underline ? { borderTopColor: "transparent", borderRightColor: "transparent", borderLeftColor: "transparent" } : {}),
                [`${Input}`]: {
                    outline: "none",
                },
            }),
        }),
        "&:disabled": {
            pointerEvents: "none",
            opacity: 0.5,
        },
        "::placeholder": {
            ...getColor(outline, underline, outlineKeepOnHover, color, clean, theme),
            opacity: 0.3,
        },
        "::-ms-input-placeholder": {
            ...getColor(outline, underline, outlineKeepOnHover, color, clean, theme),
            opacity: 0.3,
        },
    }),
);

export const Input = styled("input").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["size"]),
})<InputProps>(({ size, theme }) => ({
    textDecoration: "none !important",
    backgroundColor: "inherit",
    borderColor: "transparent",
    ...(size === "xs" ? { lineHeight: theme.lineHeight["5"] } : { lineHeight: theme.lineHeight["6"] }),
    fontWeight: theme.fontWeight.medium,
    outline: "none",
    width: "100%",
    whiteSpace: "nowrap",
    '&[type="number"]::-webkit-outer-spin-button': {
        "-webkit-appearance": "none",
        margin: 0,
    },
    '&[type="number"]::-webkit-inner-spin-button': {
        "-webkit-appearance": "none",
        margin: 0,
    },
    '&[type="number"]': {
        "-moz-appearance": "textfield",
    },
}));

export const Textarea = styled("textarea").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["size", "resize", "minRows"]),
})<InputProps>(({ size, resize, minRows, theme }) => ({
    textDecoration: "none !important",
    backgroundColor: "transparent",
    borderColor: "transparent",
    resize: resize,
    height: size === "xs" ? `calc(${theme.lineHeight[5]} * ${minRows})` : `calc(${theme.lineHeight[6]} * ${minRows})`,
    lineHeight: size === "xs" ? theme.lineHeight[5] : theme.lineHeight[6],
    fontWeight: theme.fontWeight.medium,
    outline: "none",
    width: "100%",
}));

export const Indicators = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    gap: theme.spacing[1],
}));
